import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Calendar,
  User,
  Clock,
  Edit,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Phone,
  X,
  FileText,
  UserPlus,
  ChevronDown,
  Stethoscope,
  FilterIcon,
} from 'lucide-react';
import {
  getAllAppointments,
  getPatients, getUpcomingAppointments,
  updateAppointment,
} from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import EditAppointmentModal from '@/components/Appointment/EditAppointmentModal';
import PatientSearchDialog from '@/components/Patient/PatientSearchDialog';
import AddPatientForm from '@/components/Patient/AddPatientForm';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import { Appointment } from '@/types/appointment';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  email?: string;
}

interface AppointmentWithPatientData extends Appointment {
  patientName: string;
  patientAge: number;
  patientGender: string;
  patientPhone: string;
  appointmentDate: Date;
  appointmentTime: string;
  isToday: boolean;
  isPast: boolean;
  isUpcoming: boolean;
  timeDiff: number;
  status: 'scheduled' | 'checked_in' | 'completed' | 'cancelled' | 'no_show';
  reason: string;
  patient_id: string;
}

interface FilterOptions {
  dateRange: 'today' | 'tomorrow' | 'week' | 'month' | 'custom';
  customStartDate?: string;
  customEndDate?: string;
  status: 'all' | 'scheduled' | 'completed' | 'cancelled';
  search: string;
}

const STATUS_CONFIG = {
  scheduled: {
    label: 'Scheduled',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    dot: 'bg-blue-500',
  },
  completed: {
    label: 'Completed',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    dot: 'bg-emerald-500',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    dot: 'bg-red-400',
  },
};

export default function AppointmentsPage() {
  useEffect(() => {
    document.title = 'Appointments | OneCheckUp';
  }, []);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { currentUser } = useAuth();
  const isStaff = currentUser?.role === 'staff';

  const [currentModal, setCurrentModal] = useState<
    'none' | 'appointment' | 'patientSearch' | 'newPatient' | 'editAppointment'
  >('none');
  const [modalData, setModalData] = useState({
    selectedPatientId: '',
    editingAppointment: null as AppointmentWithPatientData | null,
    isResettingState: false,
  });

  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: 'today',
    status: 'all',
    search: '',
  });

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const openAppointmentModal = useCallback(() => {
    setCurrentModal('appointment');
    setModalData(prev => ({ ...prev, selectedPatientId: '', editingAppointment: null }));
  }, []);
  const openPatientSearchModal = useCallback(() => setCurrentModal('patientSearch'), []);
  const openNewPatientModal = useCallback(() => setCurrentModal('newPatient'), []);
  const openEditAppointmentModal = useCallback((apt: AppointmentWithPatientData) => {
    setCurrentModal('editAppointment');
    setModalData(prev => ({ ...prev, editingAppointment: apt }));
  }, []);
  const closeAllModals = useCallback(() => {
    setCurrentModal('none');
    setModalData({ selectedPatientId: '', editingAppointment: null, isResettingState: false });
  }, []);

  const handlePatientSelected = useCallback((patient: Patient) => {
    setPatients(prev => (prev.some(p => p.id === patient.id) ? prev : [patient, ...prev]));
    setModalData(prev => ({ ...prev, selectedPatientId: patient.id }));
    setCurrentModal('appointment');
  }, []);

  const handlePatientCreated = useCallback((newPatient: Patient) => {
    setPatients(prev => [newPatient, ...prev]);
    setModalData(prev => ({ ...prev, selectedPatientId: newPatient.id }));
    setCurrentModal('appointment');
    toast({ title: 'Patient created', description: `${newPatient.name} selected for appointment` });
  }, []);

  const handleAppointmentAdded = useCallback(() => {
    loadData();
    closeAllModals();
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────
  const loadData = async () => {
    try {
      if (!loading) setRefreshing(true);
      if (!currentUser?.clinic_id) {
        setPatients([]);
        setAppointments([]);
        return;
      }
      const [aptsData, ptsData] = await Promise.all([
        getUpcomingAppointments(30, currentUser.doctor_id ?? undefined, currentUser.clinic_id),
        getPatients({ clinic_id: currentUser.clinic_id }),
      ]);
      if (aptsData) {
        setAppointments(
          aptsData.sort((a, b) => {
            const da = new Date(`${a.date.split('T')[0]} ${a.time}`);
            const db = new Date(`${b.date.split('T')[0]} ${b.time}`);
            return da.getTime() - db.getTime();
          })
        );
      }
      if (ptsData?.patients) setPatients(ptsData.patients);
      setError(null);
    } catch (err) {
      setError('Failed to load appointments.');
      handleError(err, 'Appointments Loading');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    if (searchParams.get('filter') === 'today')
      setFilters(prev => ({ ...prev, dateRange: 'today' }));
  }, [searchParams]);

  // ── Parsing ───────────────────────────────────────────────────────────────
  const parseAppointment = useCallback(
    (apt: Appointment): AppointmentWithPatientData => {
      const patient = patients.find(p => p.id === apt.patient_id) || {
        id: '',
        name: (apt as any).patient_name || 'Unknown',
        age: (apt as any).patient_age || 0,
        gender: (apt as any).patient_gender || '',
        phone: (apt as any).patient_phone || '',
      };
      const dateOnly = apt.date.split('T')[0];
      const aptDateTime = new Date(`${dateOnly} ${apt.time}`);
      const now = new Date();
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      const aptDate = new Date(apt.date);
      const isToday = aptDate.toDateString() === todayMidnight.toDateString();
      const isPast = aptDateTime < now;
      const isUpcoming = aptDateTime > now && isToday;
      const timeDiff = aptDateTime.getTime() - now.getTime();
      return {
        ...apt,
        patientName: patient.name,
        patientAge: patient.age,
        patientGender: patient.gender,
        patientPhone: patient.phone,
        appointmentDate: aptDate,
        appointmentTime: apt.time,
        isToday,
        isPast,
        isUpcoming,
        timeDiff,
        status: apt.status as 'scheduled' | 'completed' | 'cancelled',
        reason: apt.reason,
        patient_id: apt.patient_id,
      };
    },
    [patients]
  );

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = appointments.map(parseAppointment);
    const today = new Date(); today.setHours(0, 0, 0, 0);

    switch (filters.dateRange) {
      case 'today':
        list = list.filter(a => a.isToday); break;
      case 'tomorrow': {
        const tmr = new Date(today); tmr.setDate(tmr.getDate() + 1);
        list = list.filter(a => a.appointmentDate.toDateString() === tmr.toDateString()); break;
      }
      case 'week': {
        const wEnd = new Date(today); wEnd.setDate(wEnd.getDate() + 7);
        list = list.filter(a => a.appointmentDate >= today && a.appointmentDate <= wEnd); break;
      }
      case 'month': {
        const mEnd = new Date(today); mEnd.setMonth(mEnd.getMonth() + 1);
        list = list.filter(a => a.appointmentDate >= today && a.appointmentDate <= mEnd); break;
      }
      case 'custom':
        if (filters.customStartDate && filters.customEndDate) {
          const s = new Date(filters.customStartDate), e = new Date(filters.customEndDate);
          list = list.filter(a => a.appointmentDate >= s && a.appointmentDate <= e);
        }
        break;
    }
    if (filters.status !== 'all') list = list.filter(a => a.status === filters.status);
    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      list = list.filter(
        a =>
          a.patientName.toLowerCase().includes(q) ||
          a.patientPhone.includes(q) ||
          (a.reason && a.reason.toLowerCase().includes(q))
      );
    }
    return list;
  }, [appointments, filters, parseAppointment]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const all = appointments.map(parseAppointment);
    const todayApts = all.filter(a => a.isToday);
    return {
      total: todayApts.length,
      scheduled: todayApts.filter(a => a.status === 'scheduled').length,
      completed: todayApts.filter(a => a.status === 'completed').length,
      cancelled: todayApts.filter(a => a.status === 'cancelled').length,
    };
  }, [appointments, parseAppointment]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCancel = async (apt: AppointmentWithPatientData) => {
    if (!window.confirm(`Cancel appointment for ${apt.patientName}?`)) return;
    try {
      await updateAppointment({ ...apt, status: 'cancelled', updated_at: new Date() });
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'cancelled' } : a));
      toast({ title: 'Cancelled', description: `${apt.patientName}'s appointment cancelled` });
    } catch {
      toast({ title: 'Error', description: 'Failed to cancel', variant: 'destructive' });
    }
  };

  // ── Formatters ────────────────────────────────────────────────────────────
  const fmtTime = (t: string) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const d = new Date(); d.setHours(h, m);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const fmtDate = (d: Date) =>
    d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  const fmtRemaining = (diff: number) => {
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // ── States ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-7 h-7 text-primary animate-spin mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading appointments...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6">
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={loadData}>Try Again</Button>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="prescription-form-container">
    {/*<div className="p-6 max-w-5xl mx-auto space-y-5">*/}
      <div className="space-y-6">

      {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Calendar className="w-6 h-6 text-primary"/>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">Appointment Management</h1>
              <p className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={refreshing}
            className="gap-2"
            title="Refresh appointments"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={openAppointmentModal} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Appointment
          </Button>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-foreground' },
          { label: 'Scheduled', value: stats.scheduled, color: 'text-blue-600' },
          { label: 'Completed', value: stats.completed, color: 'text-emerald-600' },
          { label: 'Cancelled', value: stats.cancelled, color: 'text-red-500' },
        ].map(s => (
          <div key={s.label} className="bg-card border border-border rounded-xl p-3 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="bg-card border border-border rounded-xl p-3">
        <div className="flex items-center gap-3 flex-wrap">

          {/* Date tabs */}
          <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
            {(['today', 'tomorrow', 'week', 'month'] as const).map(r => (
              <button
                key={r}
                onClick={() => setFilters(prev => ({ ...prev, dateRange: r }))}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  filters.dateRange === r
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search patient or phone..."
              value={filters.search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-8 pr-3 py-2 text-sm border border-input rounded-lg bg-background focus:outline-none focus:border-ring"
            />
          </div>

          {/* Status filter */}
          <select
            value={filters.status}
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value as FilterOptions['status'] }))}
            className="text-sm border border-input rounded-lg px-3 py-2 bg-background focus:outline-none focus:border-ring"
          >
            <option value="all">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowFiltersPanel(!showFiltersPanel)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${
              showFiltersPanel
                ? 'border-primary text-primary bg-primary/5'
                : 'border-input text-muted-foreground hover:text-foreground'
            }`}
          >
            <FilterIcon className="w-3.5 h-3.5" />
            Filters
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFiltersPanel ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Custom date range */}
        {showFiltersPanel && (
          <div className="mt-3 pt-3 border-t border-border flex items-center gap-3 flex-wrap">
            <span className="text-xs text-muted-foreground">Custom range:</span>
            <input
              type="date"
              value={filters.customStartDate || ''}
              onChange={e => setFilters(prev => ({ ...prev, customStartDate: e.target.value, dateRange: 'custom' }))}
              className="text-xs border border-input rounded-lg px-2 py-1.5 bg-background"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              value={filters.customEndDate || ''}
              onChange={e => setFilters(prev => ({ ...prev, customEndDate: e.target.value, dateRange: 'custom' }))}
              className="text-xs border border-input rounded-lg px-2 py-1.5 bg-background"
            />
            <button
              onClick={() => setFilters({ dateRange: 'today', status: 'all', search: '' })}
              className="text-xs text-muted-foreground hover:text-foreground ml-auto"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* ── Results count ── */}
      <p className="text-xs text-muted-foreground px-1">
        {filtered.length} appointment{filtered.length !== 1 ? 's' : ''}
        {filters.dateRange === 'today' ? ' today' : ''}
      </p>

      {/* ── Appointment List ── */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Calendar className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No appointments found</p>
          <p className="text-xs text-muted-foreground/60 mt-1 mb-4">
            {filters.search || filters.status !== 'all'
              ? 'Try adjusting your filters'
              : 'No appointments scheduled for this period'}
          </p>
          <Button size="sm" onClick={openAppointmentModal} className="gap-2">
            <Plus className="w-4 h-4" />
            Schedule Appointment
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(apt => {
            const cfg = STATUS_CONFIG[apt.status];
            const remaining = apt.isToday && apt.status === 'scheduled' && !apt.isPast
              ? fmtRemaining(apt.timeDiff)
              : null;
            const isUrgent = remaining !== null && apt.timeDiff <= 900000;

            return (
              <div
                key={apt.id}
                className={`bg-card border rounded-xl p-4 transition-all hover:shadow-sm ${
                  isUrgent ? 'border-warning/40 bg-warning/5' : 'border-border'
                } ${apt.status === 'cancelled' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-4">

                  {/* Time column */}
                  <div className="text-center w-18 flex-shrink-0">
                    <p className="text-base font-bold text-foreground leading-none">
                      {fmtTime(apt.appointmentTime)}
                    </p>
                    {filters.dateRange !== 'today' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {apt.appointmentDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="w-px h-10 bg-border flex-shrink-0" />

                  {/* Patient info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-foreground capitalize">{apt.patientName}</p>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.bgColor} ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                      {remaining && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          isUrgent
                            ? 'bg-warning/20 text-warning'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {remaining} remaining
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      {(apt.patientAge || apt.patientGender) && (
                        <span>{apt.patientAge}y • {apt.patientGender}</span>
                      )}
                      {apt.patientPhone && (
                        <a
                          href={`tel:${apt.patientPhone}`}
                          onClick={e => e.stopPropagation()}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          <Phone className="w-3 h-3" />
                          {apt.patientPhone}
                        </a>
                      )}
                      {apt.reason && (
                        <span className="truncate max-w-[200px]">• {apt.reason}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Start Consultation — doctor only */}
                    {apt.status === 'scheduled' && !apt.isPast && !isStaff && (
                      <Button
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/prescriptions?patient=${apt.patient_id}&appointment=${apt.id}&mode=consultation`);
                        }}
                        className="gap-1.5 h-8 text-xs mr-1"
                      >
                        <Stethoscope className="w-3.5 h-3.5" />
                        Consult
                      </Button>
                    )}

                    {/* View Patient */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        navigate(
                          isStaff
                            ? `/frontdesk/patients/${apt.patient_id}`
                            : `/patients/${apt.patient_id}`
                        );
                      }}
                      className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="View patient"
                    >
                      <User className="w-4 h-4" />
                    </button>

                    {/* View Prescriptions — doctor only */}
                    {!isStaff && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/prescriptions?patient=${apt.patient_id}`);
                        }}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="View prescriptions"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    )}

                    {/* Edit */}
                    {apt.status === 'scheduled' && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          openEditAppointmentModal(apt);
                        }}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Edit appointment"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}

                    {/* Cancel */}
                    {apt.status === 'scheduled' && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleCancel(apt);
                        }}
                        className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        title="Cancel appointment"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modals ── */}
      {currentModal === 'appointment' && (
        <AddAppointmentModal
          isOpen
          onClose={closeAllModals}
          patientId={modalData.selectedPatientId}
          patients={patients}
          showPatientSelection
          onPatientSearch={openPatientSearchModal}
          onAdded={handleAppointmentAdded}
        />
      )}

      {currentModal === 'patientSearch' && (
        <PatientSearchDialog
          isOpen
          onClose={closeAllModals}
          onPatientSelect={handlePatientSelected}
          onCreateNew={openNewPatientModal}
          context="appointment"
          title="Select Patient for Appointment"
          description="Search for existing patient or create new one"
        />
      )}

      {currentModal === 'newPatient' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                <h2 className="text-base font-semibold">Create New Patient</h2>
              </div>
              <button
                onClick={closeAllModals}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <AddPatientForm onSuccess={handlePatientCreated} onCancel={closeAllModals} compact />
            </div>
          </div>
        </div>
      )}

      {currentModal === 'editAppointment' && modalData.editingAppointment && (
        <EditAppointmentModal
          appointment={modalData.editingAppointment}
          isOpen
          onClose={closeAllModals}
          onUpdated={updated => {
            setAppointments(prev =>
              prev.map(a => (a.id === updated.id ? (updated as Appointment) : a))
            );
            closeAllModals();
            toast({ title: 'Updated', description: 'Appointment updated successfully' });
          }}
        />
      )}
    </div>
    </div>
  );
}